import React, { useState } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import MuiTooltip from '@mui/material/Tooltip'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import { Flex } from '../../Components'
import { FC, invalidateQuery } from '../../Services'
import { useAuth } from '../../Provider/AuthProvider'
import { ConfirmNameDeleteDialog } from '../ConfirmNameDeleteDialog'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import IconButton from '@mui/material/IconButton'

const ClientsTable = ({ clients = [], configs = [] }) => {
  const isSiapAdmin = ['siapAdmin', 'siapDeveloper'].includes(useAuth()?.user?.user?.role)
  const [deleteClient, setDeleteClient] = useState(null)
  const [isDeletingClient, setIsDeletingClient] = useState(false)

  if (!clients.length) return null

  const downloadFile = (authData, fileName, configName) => {
    const configIsValid = checkConfigIsValid(configName, configs)
    if (!configIsValid.result) {
      window.growl.show({
        severity: 'error',
        summary: 'Errore',
        detail: 'Configurazione non valida. Completa i campi richiesti: ' + configIsValid.invalidDataTypes
      })
      return
    }
    const fileContent = JSON.stringify(authData, null, 2)
    const blob = new Blob([fileContent], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()

    URL.revokeObjectURL(url)
  }

  const handleConfigNameChange = async (configName, clientId) => {
    await FC.service('clients').patch(clientId, { configName })
    invalidateQuery('clients')
  }

  const onCellEditComplete = async (newRow, oldRow) => {
    try {
      if (newRow.configName !== oldRow.configName) {
        await FC.service('clients').patch(newRow._id, { configName: newRow.configName })
        invalidateQuery('clients')
        invalidateQuery('configs')
      }
    } catch (e) {
      console.log(e)
    }
    return newRow
  }

  const handleDeleteClient = async () => {
    if (!deleteClient?._id) return
    setIsDeletingClient(true)
    try {
      await FC.service('clients').remove(deleteClient._id)
      window.growl.show({ severity: 'success', summary: 'Client eliminato', detail: 'Eliminazione completata.' })
      setDeleteClient(null)
      invalidateQuery('clients')
      invalidateQuery('clientsByAgency')
    } catch (err) {
      window.growl.show({ severity: 'error', summary: 'Eliminazione non riuscita', detail: err?.message || 'Errore' })
    } finally {
      setIsDeletingClient(false)
    }
  }

  const configOptions = (Array.isArray(configs) ? configs : [configs]).map(a => a.name)

  const deleteColumn = isSiapAdmin
    ? {
      field: 'delete',
      headerName: '',
      width: 64,
      minWidth: 64,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const canDelete = params.row.status === 'offline' || params.row.status === 'created'
        return (
          <MuiTooltip
            title={canDelete ? 'Elimina client' : 'Eliminazione consentita solo se il client è Creato o Offline'}
            placement='left'
          >
            <span>
              <IconButton
                size='small'
                color='error'
                disabled={!canDelete}
                onClick={() => canDelete && setDeleteClient(params.row)}
                aria-label={canDelete ? 'Elimina client' : 'Eliminazione non disponibile'}
              >
                <DeleteOutlineIcon fontSize='small' />
              </IconButton>
            </span>
          </MuiTooltip>
        )
      }
    }
    : null

  const columns = [
    {
      field: 'status',
      headerName: 'Stato',
      width: 80,
      minWidth: 80,
      sortable: false,
      renderCell: (params) => <StatusIndicator status={params.value} />
    },
    { field: '_id', headerName: 'id', flex: 1, minWidth: 120 },
    { field: 'agencyId', headerName: 'Id Agenzia', flex: 0.8, minWidth: 100 },
    { field: 'agencyName', headerName: 'Nome Agenzia', flex: 1, minWidth: 140 },
    { field: 'version', headerName: 'Versione', flex: 0.6, minWidth: 90 },
    {
      field: 'machineName',
      headerName: 'Nome macchina',
      flex: 0.8,
      minWidth: 140,
      valueGetter: (value, row) => row?.additionalInfo?.machineName || '—'
    },
    {
      field: 'platform',
      headerName: 'Piattaforma',
      flex: 0.6,
      minWidth: 100,
      valueGetter: (value, row) => row?.additionalInfo?.platform || '—'
    },
    {
      field: 'ip',
      headerName: 'IP',
      flex: 0.7,
      minWidth: 100,
      valueGetter: (value, row) => row?.additionalInfo?.ip || '—'
    },
    {
      field: 'createdAt',
      headerName: 'Data Creazione',
      flex: 1,
      minWidth: 170,
      sortable: true,
      renderCell: (params) => {
        const creationDate = new Date(params.value)
        return creationDate.toLocaleDateString('it-IT', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      }
    },
    {
      field: 'configName',
      headerName: 'Config',
      flex: 1,
      minWidth: 150,
      editable: isSiapAdmin,
      type: 'singleSelect',
      valueOptions: configOptions,
      renderCell: isSiapAdmin
        ? (params) => (
            <Stack direction='row' alignItems='center' spacing={0.5} sx={{ width: '100%' }}>
              <span>{params.value || '—'}</span>
              <EditOutlinedIcon sx={{ fontSize: 14, color: 'action.active', flexShrink: 0 }} titleAccess='Modificabile' />
            </Stack>
          )
        : undefined,
      renderEditCell: (params) => (
        <Select
          value={params.value || ''}
          onChange={(e) => {
            params.api.setEditCellValue({ id: params.id, field: params.field, value: e.target.value })
          }}
          fullWidth
          size='small'
          disabled={!isSiapAdmin}
        >
          {configOptions.map(name => (
            <MenuItem key={name} value={name}>{name}</MenuItem>
          ))}
        </Select>
      )
    },
    {
      field: 'download',
      headerName: 'File Installazione',
      flex: 1,
      minWidth: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant='contained'
          size='small'
          color='primary'
          startIcon={<DownloadIcon />}
          onClick={() => downloadFile({ strategy: 'client', token: params.row?.auth }, 'auth.json', params.row.configName)}
          sx={{ minWidth: 160, color: '#fff' }}
        >
          Scarica Auth
        </Button>
      )
    }
  ]

  const columnsWithActions = deleteColumn ? [...columns, deleteColumn] : columns

  return (
    <>
    <ConfirmNameDeleteDialog
      open={!!deleteClient}
      onClose={() => !isDeletingClient && setDeleteClient(null)}
      title='Elimina client'
      nameToMatch={deleteClient?._id || ''}
      nameFieldLabel="Digita l'ID del client per confermare"
      hint="Per sicurezza, inserisci l'ID esatto mostrato nella colonna id."
      onConfirm={handleDeleteClient}
      isProcessing={isDeletingClient}
    />
    <DataGrid
      rows={clients}
      columns={columnsWithActions}
      getRowId={(row) => row._id}
      initialState={{
        pagination: { paginationModel: { pageSize: 50 } },
        sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] }
      }}
      pageSizeOptions={[25, 50, 100]}
      disableRowSelectionOnClick
      processRowUpdate={onCellEditComplete}
      sx={{ ...dataGridSx, height: Math.max(400, Math.min(clients.length * 52 + 110, 800)) }}
    />
    </>
  )
}

const checkConfigIsValid = (configName, configs) => {
  const agencySelectedConfig = configName
  const selectedConfig = configs.filter((config) => config.name === agencySelectedConfig)[0]
  if (!selectedConfig) return false
  if (selectedConfig?.filters?.length === 0) return true
  const hasInvalidPaths = selectedConfig?.filters?.filter((filter) => !filter.path || !filter.path.length > 0).length > 0
  const hasZeroExtensions = selectedConfig?.filters?.filter((filter) => !filter.fileExtensionsToInclude || !filter.fileExtensionsToInclude.length > 0).length > 0
  const hasEmptyDocTypes = selectedConfig?.filters?.filter((filter) => !filter.docType || filter.docType.length === 0).length > 0

  let invalidDataTypes = hasInvalidPaths ? ['Nessuno Paths'] : ['']
  invalidDataTypes.push(hasZeroExtensions ? 'Nessuna estensione' : '')
  invalidDataTypes.push(hasEmptyDocTypes ? 'Nessun tipo di documento' : '')
  invalidDataTypes = invalidDataTypes.filter((item) => item !== '').join(', ')

  return { result: !(hasInvalidPaths || hasZeroExtensions || hasEmptyDocTypes), invalidDataTypes }
}

const StatusIndicator = ({ status }) => {
  const statusColors = {
    running: '#4CAF50',
    maintenance: '#9c27b0',
    offline: '#F44336'
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      <MuiTooltip
        title={status || ''}
        placement='top'
      >
        <div
          style={{
            height: '1.3em',
            width: '1.3em',
            border: '1px solid transparent',
            borderRadius: '50%',
            backgroundColor: statusColors[status] || '#FFC107',
            cursor: 'pointer'
          }}
        />
      </MuiTooltip>
    </div>
  )
}

const dataGridSx = {
  border: 'none',
  width: '100%',
  marginTop: '1em',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#004680',
    color: '#F5F5F5',
    borderRadius: 0
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    color: '#32324E',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center'
  },
  '& .MuiDataGrid-cell--editable': {
    cursor: 'pointer',
    backgroundColor: 'rgba(0, 70, 128, 0.06)',
    '&:hover': {
      backgroundColor: 'rgba(0, 70, 128, 0.1)'
    }
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(0, 70, 128, 0.04)'
  },
  '& .MuiDataGrid-footerContainer': {
    backgroundColor: 'transparent'
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none'
  }
}

export default ClientsTable
